/**
 * ThreadCaMount — a thread's linked CA item (RFI or Submittal), mounted inline
 * under the ThreadNode (Round 7).
 *
 * The schedule-side workflow: RFIs/Submittals live as threads under
 * Schedule > Construction Support > Threads; expanding the thread reveals the
 * canonical RfiNode/SubmittalNode exactly as it appears in
 * Reference > Construction (One-Component-Per-Node). The reverse direction
 * (CA item → thread) is CaThreadMount in ConstructionSection.
 *
 * Lookup: Supabase reverse query by thread_airtable_id (getRfiByThread /
 * getSubmittalByThread), cached in ctx.threadCaItemCache[threadId] as
 * { kind: 'rfi'|'submittal', item } or false (no match). The contract record
 * comes from ctx.contractCache (loaded on demand).
 *
 * Renders null for thread types that aren't 'RFIs'/'Submittals'.
 *
 * Import-cycle note: ConstructionSection → ScheduleSection → ThreadNode →
 * ThreadCaMount → ConstructionSection. Safe: every cross-module reference is
 * render-time against hoisted function declarations.
 */

import { useEffect, useRef } from 'react'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { getRfiByThread } from '../services/rfis.js'
import { getSubmittalByThread } from '../services/submittals.js'
import { getProjectContracts } from '../services/bidCampaigns.js'
import { RfiNode, SubmittalNode } from './ConstructionSection.jsx'

function ensureContractsLoaded(projId) {
  if (!projId) return
  if (ctx.contractCache !== undefined) return
  ctx.contractCache = null
  getProjectContracts(projId).then(c => {
    ctx.contractCache = c
    window.dispatchEvent(new Event('outline-render'))
  }).catch(() => {
    ctx.contractCache = []
    window.dispatchEvent(new Event('outline-render'))
  })
}

export default function ThreadCaMount({ item, projectId }) {
  const type = item?.fields?.['Type'] || ''
  const threadId = item?.id || ''
  const isCa = type === 'RFIs' || type === 'Submittals'
  const loadedRef = useRef(false)

  function fetchCaItem() {
    if (!ctx.threadCaItemCache) ctx.threadCaItemCache = {}
    if (ctx.threadCaItemCache[threadId] !== undefined) return
    ctx.threadCaItemCache[threadId] = null // in-flight
    const lookup = type === 'RFIs'
      ? getRfiByThread(threadId).then(rfi => rfi ? { kind: 'rfi', item: rfi } : false)
      : getSubmittalByThread(threadId).then(s => s ? { kind: 'submittal', item: s } : false)
    lookup.then(result => {
      if (!ctx.threadCaItemCache) ctx.threadCaItemCache = {}
      ctx.threadCaItemCache[threadId] = result
      if (result && result.item?.project_id) ensureContractsLoaded(result.item.project_id)
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.threadCaItemCache) ctx.threadCaItemCache = {}
      ctx.threadCaItemCache[threadId] = false
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  // Lazy-load the linked CA item once per thread (hooks before any return)
  useEffect(() => {
    if (!isCa || !threadId || loadedRef.current) return
    loadedRef.current = true
    fetchCaItem()
  }, [isCa, threadId, type]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch after a cache bust (e.g. onChanged from an edit made via this
  // mount) — the standard outline-render listener pattern.
  useEffect(() => {
    if (!isCa || !threadId) return
    function onRender() {
      if (!loadedRef.current) return
      if (ctx.threadCaItemCache?.[threadId] === undefined) fetchCaItem()
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [isCa, threadId, type]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isCa || !threadId) return null

  const entry = ctx.threadCaItemCache?.[threadId]
  if (entry === undefined || entry === null) return null // not started / in-flight — quiet
  if (entry === false) return null // no CA item linked to this thread

  const caItem = entry.item
  // RFIs and Submittals belong to their milestone, not a thread (Matt, 2026-09-24). A leftover
  // thread must not mount one: that item mounts its milestone, whose passenger is this thread,
  // which mounted the item again — an endless nesting that froze and crashed the page.
  if (caItem && caItem.milestone_airtable_id) return null
  const contracts = Array.isArray(ctx.contractCache) ? ctx.contractCache : []
  const contract = contracts.find(c => c.id === caItem.contract_id)
    || { id: caItem.contract_id, project_id: caItem.project_id || projectId || wf.zoomProjectId || '' }

  function onChanged() {
    // Bust both sides so edits made from this mount reflect everywhere
    if (ctx.threadCaItemCache) ctx.threadCaItemCache[threadId] = undefined
    if (ctx.rfiCache && caItem.contract_id) ctx.rfiCache[caItem.contract_id] = undefined
    if (ctx.submittalCache && caItem.contract_id) ctx.submittalCache[caItem.contract_id] = undefined
    window.dispatchEvent(new Event('outline-render'))
  }

  return entry.kind === 'rfi'
    ? <RfiNode rfi={caItem} contract={contract} onChanged={onChanged} hideThreadMount mountCtx="th" />
    : <SubmittalNode submittal={caItem} contract={contract} onChanged={onChanged} hideThreadMount mountCtx="th" />
}
